/**
 * ProfilePage Component
 * Tabbed profile/account page with personal data, credits, billing, and security
 */

import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardHeader } from '@/components/layout/DashboardHeader';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { User, CreditCard, FileText, Shield, LogOut } from 'lucide-react';
import { PersonalDataTab } from './tabs/PersonalDataTab';
import { CreditsTab } from './tabs/CreditsTab';
import { BillingTab } from './tabs/BillingTab';
import { SecurityTab } from './tabs/SecurityTab';
import { useAuth } from '@/hooks/useAuth';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { logout, isLoading } = useAuth();

  return (
    <DashboardLayout
      header={<DashboardHeader />}
      footer={<Footer />}
    >
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-[var(--color-charcoal)] mb-6">
          {t('profile.title')}
        </h1>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="w-full grid grid-cols-4 bg-white/60 border border-gray-100 rounded-xl p-1 mb-6 h-auto">
            <TabsTrigger value="personal" data-track="profile_tab_personal" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 px-1 sm:px-3 py-2.5 sm:py-1.5 text-[11px] sm:text-sm leading-tight text-center sm:text-left whitespace-normal data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
              <User className="w-4 h-4 shrink-0" />
              <span>{t('profile.tabs.personal')}</span>
            </TabsTrigger>
            <TabsTrigger value="credits" data-track="profile_tab_credits" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 px-1 sm:px-3 py-2.5 sm:py-1.5 text-[11px] sm:text-sm leading-tight text-center sm:text-left whitespace-normal data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
              <CreditCard className="w-4 h-4 shrink-0" />
              <span>{t('profile.tabs.credits')}</span>
            </TabsTrigger>
            <TabsTrigger value="billing" data-track="profile_tab_billing" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 px-1 sm:px-3 py-2.5 sm:py-1.5 text-[11px] sm:text-sm leading-tight text-center sm:text-left whitespace-normal data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
              <FileText className="w-4 h-4 shrink-0" />
              <span>{t('profile.tabs.billing')}</span>
            </TabsTrigger>
            <TabsTrigger value="security" data-track="profile_tab_security" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 px-1 sm:px-3 py-2.5 sm:py-1.5 text-[11px] sm:text-sm leading-tight text-center sm:text-left whitespace-normal data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
              <Shield className="w-4 h-4 shrink-0" />
              <span>{t('profile.tabs.security')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <PersonalDataTab />
          </TabsContent>

          <TabsContent value="credits">
            <CreditsTab />
          </TabsContent>

          <TabsContent value="billing">
            <BillingTab />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>
        </Tabs>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="ghost"
            data-track="profile_logout"
            onClick={logout}
            disabled={isLoading}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
          >
            <LogOut className="w-4 h-4" />
            {t('common.logout')}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
